import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface CTAButton {
  label: string;
  href: string;
  variant: 'primary' | 'secondary' | 'tertiary';
  icon?: string;
  onClick?: () => void;
}

interface Stat {
  value: string;
  label: string;
}

interface TrustSignal {
  icon: string;
  text: string;
}

interface PageHeroProps {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  eyebrow?: {
    icon?: string;
    text: string;
  };
  theme?: 'light' | 'dark' | 'warm' | 'brand' | 'gradient-slate' | 'gradient-warm';
  ctas?: CTAButton[];
  stats?: Stat[];
  trustSignals?: TrustSignal[];
  backgroundImage?: string;
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
}

const themeClasses = {
  light:           'bg-gradient-to-br from-[#FDFAF4] via-white to-[#F7F5F0]',
  dark:            'bg-[#1E1A14]',
  warm:            'bg-gradient-to-br from-[#1E1A14] via-[#2C2518] to-[#1E1A14]',
  brand:           'bg-gradient-to-br from-[#F7F5F0] via-white to-[#F7F5F0]',
  'gradient-slate':'bg-gradient-to-br from-[#FDFAF4] via-white to-[#F7F5F0]',
  'gradient-warm': 'bg-gradient-to-br from-[#1E1A14] via-[#2C2518] to-[#1E1A14]',
};

export default function PageHero({
  title,
  subtitle,
  eyebrow,
  theme = 'light',
  ctas = [],
  stats = [],
  trustSignals = [],
  backgroundImage,
  children,
  className = '',
  contentClassName = '',
}: PageHeroProps) {
  const isDark = theme === 'dark' || theme === 'warm' || theme === 'gradient-warm';
  const textColor = isDark ? 'text-[#FDF6E3]' : 'text-[#1A2332]';
  const subtitleColor = isDark ? 'text-[rgba(253,246,227,0.65)]' : 'text-[#4A5568]';

  return (
    <section className={`relative ${themeClasses[theme]} pt-28 sm:pt-32 pb-14 sm:pb-20 overflow-hidden ${className}`}>
      {/* Background Image */}
      {backgroundImage && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: `url('${backgroundImage}')` }}
          />
          {isDark && <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/10 to-black/20" />}
        </>
      )}

      {/* Subtle warm texture for dark themes */}
      {isDark && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 70% 0%, rgba(245,200,66,0.06) 0%, transparent 60%)' }}
          aria-hidden="true"
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center max-w-4xl mx-auto ${contentClassName}`}>

          {/* Eyebrow Badge */}
          {eyebrow && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-5 sm:mb-6"
              style={
                isDark
                  ? { background: 'rgba(245,200,66,0.10)', border: '1px solid rgba(245,200,66,0.22)', color: '#F5C842' }
                  : { background: 'rgba(26,35,50,0.07)', border: '1px solid rgba(26,35,50,0.14)', color: '#1A2332' }
              }
            >
              {eyebrow.icon && (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${eyebrow.icon} text-base`} style={{ color: isDark ? '#F5C842' : '#D97706' }}></i>
                </div>
              )}
              <span>{eyebrow.text}</span>
            </div>
          )}

          {/* Title */}
          <h1
            className={`text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-5 sm:mb-6 leading-tight ${textColor}`}
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <div className={`text-base sm:text-xl mb-6 sm:mb-8 leading-relaxed ${subtitleColor}`}>
              {subtitle}
            </div>
          )}

          {/* Trust Signals */}
          {trustSignals.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-6 sm:mb-8">
              {trustSignals.map((signal, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2"
                  style={{ color: isDark ? 'rgba(253,246,227,0.80)' : '#4A5568' }}
                >
                  <i
                    className={`${signal.icon} text-lg flex-shrink-0`}
                    style={{ color: isDark ? '#F5C842' : '#D97706' }}
                  ></i>
                  <span className="text-sm sm:text-base font-medium">{signal.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* CTAs */}
          {ctas.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              {ctas.map((cta, idx) => {
                const primaryStyle = isDark
                  ? { background: '#F5C842', color: '#1E1A14', boxShadow: '0 4px 18px rgba(245,200,66,0.30)' }
                  : { background: '#D97706', color: '#ffffff', boxShadow: '0 4px 18px rgba(217,119,6,0.28)' };

                const secondaryStyle = isDark
                  ? { background: 'rgba(245,200,66,0.10)', border: '1.5px solid rgba(245,200,66,0.28)', color: '#FDF6E3' }
                  : { background: '#ffffff', border: '1.5px solid #1A2332', color: '#1A2332' };

                const buttonStyle =
                  cta.variant === 'primary' ? primaryStyle
                  : cta.variant === 'secondary' ? secondaryStyle
                  : primaryStyle;

                const content = (
                  <>
                    {cta.icon && <i className={`${cta.icon} text-xl shrink-0`}></i>}
                    {cta.label}
                  </>
                );

                const baseClass = 'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer';

                return cta.onClick ? (
                  <button
                    key={idx}
                    onClick={cta.onClick}
                    className={baseClass}
                    style={buttonStyle}
                  >
                    {content}
                  </button>
                ) : (
                  <Link
                    key={idx}
                    to={cta.href}
                    className={baseClass}
                    style={buttonStyle}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Custom Children */}
          {children}

          {/* Stats Grid */}
          {stats.length > 0 && (
            <div
              className={`grid gap-4 sm:gap-8 mt-12 sm:mt-16 pt-8 sm:pt-12`}
              style={{
                gridTemplateColumns: `repeat(${Math.min(stats.length, 3)}, minmax(0, 1fr))`,
                borderTop: isDark ? '1px solid rgba(245,200,66,0.15)' : '1px solid #E8E2D6',
              }}
            >
              {stats.map((stat, idx) => (
                <div key={idx}>
                  <div
                    className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2"
                    style={{ color: isDark ? '#F5C842' : '#D97706', fontFamily: "'Oswald', sans-serif" }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs sm:text-sm"
                    style={{ color: isDark ? 'rgba(253,246,227,0.55)' : '#6B7280' }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
